import type { Address } from "@/features/machines/types";
import type { ProductionHistoryWindow } from "@/features/devices/types";
import type { ProductionTotalMode } from "./productionSources";
import type { GhmSignalKind } from "@ghm/mqtt-contract";

export type WidgetType =
  | "number"
  | "led"
  | "state-value"
  | "gauge"
  | "progress"
  | "availability"
  | "utilization"
  | "production-status"
  | "production-kpi"
  | "line-chart"
  | "bar-chart"
  /**
   * Painel de N grandezas comparaveis lado a lado (tensao L1/L2/L3). Exige
   * `extraSources`; com uma fonte so degrada para um numero grande, e o
   * editor impede salvar nesse estado.
   */
  | "multi-value"
  /** N series no MESMO eixo. Mesma exigencia de fontes do `multi-value`. */
  | "multi-chart"
  /**
   * Tanque/nivel: desenha o preenchimento entre `min` e `max` declarados.
   * Escolha EXPLICITA do operador — nao nasce automaticamente so porque a
   * unidade e `%`, porque nem toda porcentagem e um nivel.
   */
  | "tank"
  /**
   * Saude do barramento Modbus de um device: quantos pontos em ok, timeout,
   * excecao e quadro invalido. Card de DEVICE, nao de sinal — a ancora serve
   * so para identificar o slot.
   */
  | "modbus-health";

/** Tipos que so fazem sentido com mais de uma fonte. */
export const MULTI_SOURCE_WIDGET_TYPES: readonly WidgetType[] = ["multi-value", "multi-chart"];

export function requiresMultipleSources(widgetType: WidgetType): boolean {
  return MULTI_SOURCE_WIDGET_TYPES.includes(widgetType);
}

export type WidgetTone = "ok" | "warn" | "danger" | "info" | "neutral";

/** Interpretacao EXPLICITA de um codigo publicado; nunca um threshold. */
export interface StateMapping {
  value: number;
  label: string;
  tone: WidgetTone;
}

/** Forma do gráfico temporal. `line` para ritmo/acumulado, `bar` para produção por período. */
export type ChartKind = "line" | "bar";

/**
 * Qual série a partir do histórico Timescale o gráfico plota:
 *  - `rate`       — velocidade instantânea (`vAvg` por bucket). Default de `_v`.
 *  - `throughput` — produção no período (`delta` por bucket, reset-aware). Default de `_acc`.
 *  - `cumulative` — acumulado absoluto (`accLast`). Linha que só sobe.
 */
export type ChartSeries = "rate" | "throughput" | "cumulative";

/**
 * Qualidade/estado do dado de um widget — modelo ÚNICO de confiança (fase
 * P0). Derivado num só lugar (`deriveDataQuality` em selectors) e consumido
 * por todos os widgets, para que "device online mas sem telemetria" nunca
 * mais se confunda com "device offline".
 *
 *  - `live`    — snapshot fresco (≤ WIDGET_SIGNAL_MAX_AGE_MS). Valor confiável.
 *  - `stale`   — havia leitura, mas está velha. Último valor conhecido, não atual.
 *  - `waiting` — device online, mas este sinal ainda não chegou. Normal no boot.
 *  - `offline` — device offline. Ausência esperada, não defeito.
 */
export type DataQuality = "live" | "stale" | "waiting" | "offline";

/** Diagnóstico do sinal AIN ou da transação MB, separado do frescor MQTT. */
export type SignalDiagnostic =
  | "fault"
  | "under-range"
  | "normal"
  | "over-range"
  | "modbus-never"
  | "modbus-timeout"
  | "modbus-bad-frame"
  | "modbus-exception";

/**
 * Tamanho do card no grid. `sm` é compacto (~96px), `md` é o default
 * (~138px), `lg` ocupa 2 colunas + valor maior. Storage antigo com `wide`
 * é convertido para `lg` na normalização.
 */
export type WidgetSize = "sm" | "md" | "lg";

export interface WidgetThreshold {
  at: number;
  tone: Exclude<WidgetTone, "neutral">;
  /** Ausente = compatibilidade legada, dispara quando o valor fica acima/igual. */
  direction?: "above" | "below";
  label?: string;
}

export interface WidgetSettings {
  /**
   * O operador DIGITOU este titulo, em vez de aceitar o que foi preenchido
   * sozinho.
   *
   * Sem essa distincao um titulo automatico e indistinguivel de uma escolha, e
   * foi assim que "Producao atual - Sinal" — um default de uma versao antiga,
   * que o codigo de hoje nem gera mais — virou o NOME do sinal de um sensor de
   * porta cujo `/meta` declara "Porta do painel — Transicoes". Uma vez gravado,
   * nada mais o alcancava.
   *
   * Ausente (widget antigo, starter, titulo aceito como veio) significa "nao e
   * escolha": o nome publicado pelo dispositivo vence, e a correcao chega
   * sozinha quando o `/meta` muda.
   */
  renamedByOperator?: boolean;
  /**
   * Semantica declarada pelo hardware para a fonte ancora. Nao e editavel:
   * acompanha o widget para que uma leitura `_v` de tensao/temperatura nao
   * seja tratada como velocidade depois de persistir ou gerar um grafico.
   */
  signalKind?: GhmSignalKind;
  unit?: string;
  min?: number;
  max?: number;
  decimals?: number;
  thresholds?: WidgetThreshold[];
  activeWhen?: 0 | 1;
  stateLabels?: {
    on: string;
    off: string;
    unavailable: string;
  };
  stateMappings?: StateMapping[];
  /**
   * Quando true e o `tone` resolvido for `danger`, o card aplica a classe
   * `.w--alarm` no CSS — borda animada + ícone pulsante. Reservado para
   * cenários onde o operador quer notar imediatamente que o limite crítico
   * foi cruzado.
   */
  alarmEnabled?: boolean;
  availability?: {
    mode: "state" | "activity";
    window: "current" | ProductionHistoryWindow;
    activeWhen?: 0 | 1;
    minActiveValue?: number;
  };
  production?: {
    mode: "rate" | "hourly" | "total";
    multiplier?: number;
    target?: number;
    window?: "current" | ProductionHistoryWindow;
    /** Semantica do total atual; ausente em widgets legados. */
    totalMode?: ProductionTotalMode;
  };
  history?: {
    window: ProductionHistoryWindow;
  };
  /**
   * Cortes de sinalização da disponibilidade (widget `utilization`). Define o
   * que é boa/média/baixa utilização POR PROCESSO: `goodAtPercent` (verde a
   * partir de) e `warnAtPercent` (vermelho abaixo de). Ausente = padrão 60/30.
   * É uma meta local — quando o L3 (metas/turno) chegar, vira a fonte única.
   */
  /**
   * De onde este grafico veio, para poder desfazer.
   *
   * "Gerar grafico" SUBSTITUI o card de origem — decisao de produto, nao
   * defeito. Substituicao sem volta e que seria defeito: o operador troca um
   * numero por uma serie, ve que nao era o que queria, e nao tem como voltar
   * senao remontando o card na mao.
   *
   * O snapshot guarda a configuracao ANTES da troca. Settings e layout do
   * grafico podem mudar sem reescrever o card que "Ocultar" deve restaurar.
   * Sem snapshot (legado), so e possivel devolver os campos que foram salvos.
   */
  chartOrigin?: {
    widgetType: WidgetType;
    title: string;
    /** Janela salva por versoes anteriores ao snapshot completo. */
    history?: { window: ProductionHistoryWindow };
    snapshot?: {
      version: 1;
      source: string;
      extraSources?: string[];
      section?: string;
      layout: WidgetLayout;
      /** Nao aninhar snapshots ao gerar outra face de um card. */
      settings: Omit<WidgetSettings, "chartOrigin">;
    };
  };
  utilization?: {
    goodAtPercent: number;
    warnAtPercent: number;
  };
  /**
   * Alarme de PARADA para widgets de movimento/estado (ritmo, estado de
   * produção, LED): quando a máquina fica parada além da tolerância, o card
   * vira "perigo" (vermelho + borda pulsante). `afterMinutes: 0` = imediato.
   * Só dispara com leitura fresca — parada ≠ sem sinal.
   */
  stopAlarm?: {
    enabled: boolean;
    afterMinutes: number;
  };
  /**
   * Configuração do gráfico temporal (widgets `line-chart`/`bar-chart`).
   * Ausente em widgets legados — o renderer cai num fallback derivado do
   * sinal de origem + `history.window` para não quebrar layouts salvos.
   */
  chart?: {
    kind: ChartKind;
    series: ChartSeries;
    /** Janela móvel do eixo X. A resolução (1m/1h/1d) é derivada, não exposta. */
    window: ProductionHistoryWindow;
    /** `auto` ajusta o eixo Y aos dados; `fixed` usa `min`/`max`. */
    yScale: "auto" | "fixed";
  };
}

export interface AccumulatorDeltaValue {
  currentAcc: number;
  previousAcc: number | null;
  delta: number | null;
  elapsedSeconds: number | null;
  ratePerMinute: number | null;
  measuredAt: string | null;
  status: "warming" | "ready" | "reset";
}

export interface WidgetLayout {
  size: WidgetSize;
  order: number;
  /**
   * `true` quando o operador escolheu o tamanho no editor. Enquanto ausente, o
   * span de colunas vem do DEFAULT por tipo (compacto = 1, gráfico = 2),
   * preservando layouts salvos antes do redimensionamento. Ao escolher, o
   * `size` passa a valer (pequeno = 1, médio = 2, grande = 3 colunas).
   */
  sizeExplicit?: boolean;
  /**
   * Tamanho em CÉLULAS da malha (ver `cellGrid.ts`). É o que o operador
   * define arrastando o canto do card.
   *
   * Ausente = layout salvo antes da malha: `widgetCells` converte a partir do
   * `size` antigo, para ninguém perder a escolha que já tinha feito.
   */
  w?: number;
  h?: number;
  /**
   * Posicao na malha, em celulas e base zero. Ausente = o card ainda flui
   * automaticamente.
   *
   * O PRIMEIRO movimento congela o painel inteiro: todos os cards recebem a
   * posicao em que ja estavam. Sem isso, dar coordenada so ao card movido faz
   * os demais se reacomodarem em volta dele e saltarem sozinhos — o operador
   * mexe em um e tres mudam de lugar.
   */
  x?: number;
  y?: number;
}

export interface DashboardWidget {
  id: string;
  machineId: string;
  title: string;
  /**
   * Fonte ÂNCORA. Continua sendo uma string, e não um array, de propósito: ela
   * é a identidade do widget em todo o resto do sistema — chave da requisição
   * de histórico, `SignalConfig` que o card herda, tag conferida contra a
   * estrutura de sinais salva. Trocar por `sources[]` obrigaria 17 arquivos a
   * decidir "qual das N é a identidade?" e cada um responderia diferente.
   */
  source: string;
  /**
   * Fontes ADICIONAIS, para os widgets que comparam grandezas lado a lado
   * (painel trifásico, gráfico multissérie). Ausente = widget de fonte única,
   * que é exatamente o comportamento anterior — por isso layout salvo antes
   * desta mudança continua válido sem migração.
   *
   * Só podem entrar fontes combináveis com a âncora: mesma unidade e mesmo
   * `kind` (ver `canCombineSources`). Não é purismo — é o que garante que as
   * N séries dividam o mesmo eixo sem mentir sobre a escala.
   */
  extraSources?: string[];
  /**
   * Secao nomeada pelo operador ("Producao", "Linha 2", "Eletrico"). Agrupa
   * cards sob um cabecalho no painel.
   *
   * E DELIBERADAMENTE manual. A tentativa anterior derivava a secao da
   * semantica declarada (Estados/Ritmos/Totais/Leituras): honesto, mas numa
   * maquina com um widget de cada tipo produzia quatro cabecalhos de um card
   * — pagina comprida sem ganho nenhum. E "Producao"/"Eletrico" e informacao
   * que so quem opera a linha tem; deduzi-la do modo seria o mesmo palpite
   * que fazia todo `v` ser velocidade.
   *
   * Ausente = sem secao: o card entra na grade plana, exatamente como antes.
   */
  section?: string;
  widgetType: WidgetType;
  settings: WidgetSettings;
  layout: WidgetLayout;
}

/**
 * Todas as fontes do widget, âncora primeiro. Acessador único de propósito:
 * enquanto `source` e `extraSources` forem dois campos, todo consumidor que
 * precisa do CONJUNTO tem que passar por aqui, senão volta a existir código
 * que enxerga só a âncora e silenciosamente ignora as demais.
 */
export function widgetSources(widget: Pick<DashboardWidget, "source" | "extraSources">): string[] {
  const extras = widget.extraSources ?? [];
  return extras.length > 0 ? [widget.source, ...extras] : [widget.source];
}

/** O widget compara mais de uma grandeza? */
export function isMultiSourceWidget(
  widget: Pick<DashboardWidget, "source" | "extraSources">,
): boolean {
  return (widget.extraSources?.length ?? 0) > 0;
}

export interface ResolvedWidgetValue {
  source: string;
  address: Address;
  value: unknown;
  numericValue: number | null;
  measuredAt: string | null;
  tone: WidgetTone;
  isAvailable: boolean;
  /**
   * Estado de confiança do dado. Opcional para não quebrar construções
   * sintéticas (preview do editor, acumulador), mas SEMPRE definido no
   * caminho real (`resolveWidgetValue`). Quem renderiza deve usar
   * `quality ?? (isAvailable ? "live" : "waiting")` como fallback.
   */
  quality?: DataQuality;
  /** Presente para sinais de valor/total dos modos AIN e MB. */
  diagnostic?: SignalDiagnostic;
  accumulatorDelta?: AccumulatorDeltaValue;
}
